import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'firebase/auth';
import { ActualServer } from '../types';
import { OnboardingJourneyState } from '../hooks/useOnboardingJourney';
import { useHostedServerTlsReady } from '../hooks/useHostedServerTlsReady';
import { HostedActualServer } from '../utils/hostingApi';
import {
  createCategoriesInBudget,
  createAccountsInBudget,
  CreateCategoriesResult,
  CreateAccountsResult
} from '../utils/budgetApi';
import {
  CategoryGroupDraft,
  OnboardingAnswers,
  buildAccountsFromAnswers,
  buildCategoryGroupsFromAnswers,
  buildDebtCategoriesPayload,
  buildGoalCategoriesPayload,
  countSelectedCategories,
  createEmptyBankAccount,
  ensureDefaultGoals,
  hasMinimumAccountBalances,
  mergeDebtsWithDerived,
  toCreateCategoriesPayload
} from '../utils/onboardingCategoryBuilder';
import { getCountryName, getDefaultBankCountry } from '../utils/countries';
import {
  loadCategoryGroupsDraft,
  loadOnboardingAnswers,
  saveCategoryGroupsDraft,
  saveOnboardingAnswers
} from '../utils/onboardingStorage';
import {
  connectManagedServer,
  serversAfterConnect
} from '../utils/onboardingConnect';
import OnboardingCategoryBuilder from './OnboardingCategoryBuilder';
import OnboardingBudgetSummary from './OnboardingBudgetSummary';
import OnboardingAccountsBuilder from './onboarding/OnboardingAccountsBuilder';
import OnboardingStatementsStep from './onboarding/OnboardingStatementsStep';
import OnboardingDebtsStep from './onboarding/OnboardingDebtsStep';
import OnboardingGoalsStep from './onboarding/OnboardingGoalsStep';
import { fetchLatestExchangeRates } from '../utils/exchangeRates';
import { computeBudgetSummary } from '../utils/onboardingBudgetSummary';

interface OnboardingJourneyProps {
  user: User | null;
  activeServer: ActualServer | null;
  savedServers: ActualServer[];
  journey: OnboardingJourneyState;
  updateJourney: (changes: Partial<OnboardingJourneyState>) => void;
  onShowAuthModal: (mode?: 'signin' | 'signup') => void;
  onShowServerManagement: () => void;
  onExploreTools: () => void;
  onSaveServers: (servers: ActualServer[]) => void;
  onSetActiveServer: (serverId: string) => void;
  onValidateBudgetAccess: (
    server: ActualServer,
    budgetId: string,
    budgetPassword?: string
  ) => Promise<{ success: boolean; needsPassword: boolean; error?: string }>;
  hostingEnabled: boolean;
  hostedServer: HostedActualServer | null;
  hostedLoading: boolean;
  hostedBusy: boolean;
  isAutoProvisioning: boolean;
  provisionError: string | null;
  onRetryProvision: () => Promise<void>;
  onStartHostedServer: () => Promise<HostedActualServer>;
  onTouchHostedServer: () => Promise<HostedActualServer>;
  bootstrapPassword: string | null;
  onClearBootstrapPassword: () => void;
}

export const JourneyResumeBanner: React.FC<{
  onResume: () => void;
  onDismiss: () => void;
  /** 'start' for never-begun journeys; 'resume' after exploring / exiting mid-setup */
  mode?: 'start' | 'resume';
}> = ({ onResume, onDismiss, mode = 'resume' }) => {
  const { t } = useTranslation();
  return (
    <div className="journey-banner">
      <span className="journey-banner-text">🌱 {t('banner.text', { ns: 'onboarding' })}</span>
      <button className="journey-banner-cta" onClick={onResume}>
        {t(mode === 'start' ? 'banner.startCta' : 'banner.cta', { ns: 'onboarding' })}
      </button>
      <button
        className="journey-banner-close"
        onClick={onDismiss}
        aria-label={t('banner.dismiss', { ns: 'onboarding' })}
      >
        ×
      </button>
    </div>
  );
};

const OnboardingJourney: React.FC<OnboardingJourneyProps> = ({
  user,
  activeServer,
  savedServers,
  journey,
  updateJourney,
  onShowAuthModal,
  onShowServerManagement,
  onExploreTools,
  onSaveServers,
  onSetActiveServer,
  onValidateBudgetAccess,
  hostingEnabled,
  hostedServer,
  hostedLoading,
  hostedBusy,
  isAutoProvisioning,
  provisionError,
  onRetryProvision,
  onStartHostedServer,
  onTouchHostedServer,
  bootstrapPassword,
  onClearBootstrapPassword
}) => {
  const { t } = useTranslation();
  const {
    tlsReady
  } = useHostedServerTlsReady(hostedServer);

  const isConnected = Boolean(activeServer?.defaultBudgetId);
  const categoriesPhase = journey.categoriesPhase ?? 'questions';

  const [answers, setAnswers] = useState<OnboardingAnswers>(loadOnboardingAnswers);
  const [categoryGroups, setCategoryGroups] = useState<CategoryGroupDraft[]>(loadCategoryGroupsDraft);
  const [newCategoryInputs, setNewCategoryInputs] = useState<Record<string, string>>({});
  const [isGeneratingCategories, setIsGeneratingCategories] = useState(false);
  const [isCreatingCategories, setIsCreatingCategories] = useState(false);
  const [isCreatingAccounts, setIsCreatingAccounts] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [creationResult, setCreationResult] = useState<CreateCategoriesResult | null>(null);
  const [accountsResult, setAccountsResult] = useState<CreateAccountsResult | null>(null);
  const [debtsCategoriesResult, setDebtsCategoriesResult] = useState<CreateCategoriesResult | null>(null);
  const [isCreatingDebtCategories, setIsCreatingDebtCategories] = useState(false);
  const [debtsError, setDebtsError] = useState<string | null>(null);
  const [goalsCategoriesResult, setGoalsCategoriesResult] = useState<CreateCategoriesResult | null>(null);
  const [isCreatingGoalCategories, setIsCreatingGoalCategories] = useState(false);
  const [goalsError, setGoalsError] = useState<string | null>(null);
  const [statementFiles, setStatementFiles] = useState<Record<string, File | undefined>>({});

  const [serverPassword, setServerPassword] = useState('');
  const [budgetPassword, setBudgetPassword] = useState('');
  const [needsBudgetPassword, setNeedsBudgetPassword] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [awaitingBudget, setAwaitingBudget] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(true);
  const autoConnectAttemptedRef = useRef(false);
  const incomeSectionRef = useRef<HTMLElement>(null);

  const scrollToIncomeSection = useCallback(() => {
    incomeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    autoConnectAttemptedRef.current = false;
  }, [hostedServer?.serverUrl, bootstrapPassword]);

  useEffect(() => {
    saveOnboardingAnswers(answers);
  }, [answers]);

  // Keep budget currency aligned with the questionnaire's main spending currency
  useEffect(() => {
    if (answers.primaryCurrency && journey.budgetCurrency !== answers.primaryCurrency) {
      updateJourney({ budgetCurrency: answers.primaryCurrency });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when primary spending currency changes
  }, [answers.primaryCurrency]);

  useEffect(() => {
    saveCategoryGroupsDraft(categoryGroups);
  }, [categoryGroups]);

  const countryLabel = useCallback(
    (countryCode: string) => getCountryName(countryCode, t),
    [t]
  );

  const pendingAccounts = useMemo(
    () =>
      buildAccountsFromAnswers(answers, countryLabel).map(account => ({
        name: account.name,
        accountType: account.accountType
      })),
    [answers, countryLabel]
  );

  const namedAccounts = useMemo(
    () =>
      answers.bankAccounts.filter(
        account => account.name.trim() && account.country && account.bankId
      ),
    [answers.bankAccounts]
  );

  const hasAccountBalances = useMemo(
    () => hasMinimumAccountBalances(answers.bankAccounts),
    [answers.bankAccounts]
  );

  const resolvedBudgetCurrency =
    answers.primaryCurrency || journey.budgetCurrency || 'USD';

  // Ensure at least one bank account row when entering the accounts step
  useEffect(() => {
    if (journey.step === 4 && answers.bankAccounts.length === 0) {
      setAnswers(prev => ({
        ...prev,
        bankAccounts: [
          createEmptyBankAccount(
            0,
            getDefaultBankCountry(prev.primaryCountry),
            prev.primaryCurrency || 'USD'
          )
        ]
      }));
    }
  }, [journey.step, answers.bankAccounts.length]);

  // Sync debts from account balances when entering the debts step
  useEffect(() => {
    if (journey.step !== 6) return;
    setAnswers(prev => ({
      ...prev,
      debts: mergeDebtsWithDerived(
        prev.debts ?? [],
        prev.bankAccounts,
        prev.primaryCurrency || 'USD'
      )
    }));
  }, [journey.step]);

  // Seed default savings goal when entering the goals step
  useEffect(() => {
    if (journey.step !== 7) return;
    setAnswers(prev => ({
      ...prev,
      goals: ensureDefaultGoals(
        prev.goals,
        prev.primaryCurrency || resolvedBudgetCurrency || 'USD',
        t('steps.goals.defaultSavingsName', { ns: 'onboarding' })
      )
    }));
  }, [journey.step, resolvedBudgetCurrency, t]);
  useEffect(() => {
    if (bootstrapPassword && !serverPassword) {
      setServerPassword(bootstrapPassword);
    }
  }, [bootstrapPassword, serverPassword]);

  // Auto-advance when the account step gets completed (user signs up/in)
  useEffect(() => {
    if (journey.status === 'guided' && journey.step === 1 && user) {
      updateJourney({ step: 2, hostingChoice: 'managed' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- advance only when auth state flips
  }, [user]);

  // Mark instance ready when hosted server is active
  useEffect(() => {
    if (hostedServer?.status === 'active' && !journey.hasInstance) {
      updateJourney({ hasInstance: true, hostingChoice: 'managed' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostedServer?.status]);

  // Connection happens in the background — do not auto-advance away from categories.

  const selectedCategoryCount = useMemo(
    () => countSelectedCategories(categoryGroups),
    [categoryGroups]
  );

  const templatedCategoryCount = useMemo(
    () =>
      categoryGroups
        .filter(group => group.enabled)
        .reduce(
          (total, group) => total + group.categories.filter(category => category.note).length,
          0
        ),
    [categoryGroups]
  );

  const canOpenActual =
    hostedServer?.status === 'active' && Boolean(hostedServer.serverUrl) && tlsReady;

  const mapConnectError = (message: string) => {
    if (message === 'AUTH_REQUIRED') {
      return t('steps.activate.authRequired', { ns: 'onboarding' });
    }
    if (
      message === 'Hosted Actual servers are temporarily full. Please try again later.' ||
      message.toLowerCase().includes('temporarily full')
    ) {
      return t('steps.activate.capacityFull', { ns: 'onboarding' });
    }
    return message && message !== 'REQUEST_FAILED' && message !== 'AUTH_FAILED'
      ? message
      : t('steps.activate.connectFailed', { ns: 'onboarding' });
  };

  const handleConnect = useCallback(async (passwordOverride?: string) => {
    const passwordToUse = (passwordOverride ?? serverPassword).trim();
    if (!hostedServer?.serverUrl || !passwordToUse) {
      setConnectError(t('steps.activate.connectFailed', { ns: 'onboarding' }));
      return;
    }

    setIsConnecting(true);
    setConnectError(null);

    try {
      const result = await connectManagedServer({
        serverUrl: hostedServer.serverUrl,
        serverName: t('steps.activate.managedServerName', { ns: 'onboarding' }),
        password: passwordToUse,
        budgetPassword: needsBudgetPassword ? budgetPassword.trim() || undefined : undefined,
        budgetName: t('steps.activate.defaultBudgetName', { ns: 'onboarding' }),
        ensureBudget: true,
        validateBudgetAccess: onValidateBudgetAccess
      });

      if (result.status === 'connected') {
        const withCurrency = {
          ...result.server,
          defaultBudgetCurrency: resolvedBudgetCurrency || result.server.defaultBudgetCurrency || 'USD'
        };
        const nextServers = serversAfterConnect(savedServers, withCurrency);
        onSaveServers(nextServers);
        onSetActiveServer(withCurrency.id);
        setAwaitingBudget(false);
        setNeedsBudgetPassword(false);
        // Keep bootstrapPassword until the credentials step so the user can still copy it.
        return;
      }

      if (result.status === 'no-budgets') {
        const nextServers = serversAfterConnect(savedServers, result.server);
        onSaveServers(nextServers);
        onSetActiveServer(result.server.id);
        setAwaitingBudget(true);
        return;
      }

      if (result.status === 'needs-budget-password') {
        setNeedsBudgetPassword(true);
        setConnectError(t('steps.activate.budgetEncryptedHint', { ns: 'onboarding' }));
        return;
      }

      setConnectError(mapConnectError(result.message));
    } catch (error) {
      setConnectError(
        mapConnectError(error instanceof Error ? error.message : 'REQUEST_FAILED')
      );
    } finally {
      setIsConnecting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hostedServer?.serverUrl,
    serverPassword,
    needsBudgetPassword,
    budgetPassword,
    savedServers,
    onValidateBudgetAccess,
    onSaveServers,
    onSetActiveServer,
    journey.budgetCurrency,
    resolvedBudgetCurrency,
    t
  ]);

  // Auto-connect in the background once TLS is ready and we have a bootstrap password
  useEffect(() => {
    if (
      journey.status !== 'guided' ||
      isConnected ||
      !canOpenActual ||
      !bootstrapPassword ||
      isConnecting ||
      autoConnectAttemptedRef.current
    ) {
      return;
    }
    autoConnectAttemptedRef.current = true;
    void handleConnect(bootstrapPassword);
  }, [
    journey.status,
    isConnected,
    canOpenActual,
    bootstrapPassword,
    isConnecting,
    handleConnect
  ]);

  const copyBootstrapPassword = async () => {
    const value = bootstrapPassword || serverPassword || activeServer?.password || '';
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setPasswordCopied(true);
      window.setTimeout(() => setPasswordCopied(false), 2000);
    } catch {
      setPasswordCopied(false);
    }
  };

  const generateFromAnswers = async () => {
    const budgetCurrency = resolvedBudgetCurrency;
    setIsGeneratingCategories(true);
    setCategoriesError(null);
    try {
      const exchangeRates = await fetchLatestExchangeRates(budgetCurrency);
      const generated = buildCategoryGroupsFromAnswers(
        answers,
        t,
        countryLabel,
        new Date(),
        budgetCurrency,
        exchangeRates
      );
      setCategoryGroups(generated);
      updateJourney({ categoriesPhase: 'review' });
    } catch {
      setCategoriesError(t('steps.categories.generateFailed', { ns: 'onboarding' }));
    } finally {
      setIsGeneratingCategories(false);
    }
  };

  const toggleGroup = (groupId: string) => {
    setCategoryGroups(groups =>
      groups.map(group =>
        group.id === groupId ? { ...group, enabled: !group.enabled } : group
      )
    );
  };

  const removeCategory = (groupId: string, index: number) => {
    setCategoryGroups(groups =>
      groups.map(group =>
        group.id === groupId
          ? { ...group, categories: group.categories.filter((_, i) => i !== index) }
          : group
      )
    );
  };

  const addCategory = (groupId: string) => {
    const value = (newCategoryInputs[groupId] || '').trim();
    if (!value) return;
    setCategoryGroups(groups =>
      groups.map(group => {
        if (group.id !== groupId) return group;
        const exists = group.categories.some(
          category => category.name.toLowerCase() === value.toLowerCase()
        );
        return exists
          ? group
          : { ...group, categories: [...group.categories, { name: value }] };
      })
    );
    setNewCategoryInputs(inputs => ({ ...inputs, [groupId]: '' }));
  };

  const addCustomGroup = () => {
    const id = `custom-${Date.now()}`;
    setCategoryGroups(groups => [
      ...groups,
      { id, name: '', categories: [], enabled: true, isCustom: true }
    ]);
  };

  const renameGroup = (groupId: string, name: string) => {
    setCategoryGroups(groups =>
      groups.map(group => (group.id === groupId ? { ...group, name } : group))
    );
  };

  const removeGroup = (groupId: string) => {
    setCategoryGroups(groups => groups.filter(group => group.id !== groupId));
  };

  const handleCreateCategories = async () => {
    if (!activeServer?.defaultBudgetId) {
      if (hostedServer?.status === 'failed' || provisionError) {
        setCategoriesError(
          provisionErrorMessage ||
            hostedServer?.lastError ||
            t('steps.activate.provisionFailed', { ns: 'onboarding' })
        );
        return;
      }
      setCategoriesError(t('steps.categories.waitingToCreate', { ns: 'onboarding' }));
      return;
    }

    const payload = toCreateCategoriesPayload(categoryGroups);

    if (payload.length === 0) {
      setCategoriesError(t('steps.categories.nothingSelected', { ns: 'onboarding' }));
      return;
    }

    setIsCreatingCategories(true);
    setCategoriesError(null);

    try {
      const result = await createCategoriesInBudget(activeServer, payload, {
        replaceExisting: true
      });
      setCreationResult(result);
      const currency = answers.primaryCurrency || journey.budgetCurrency || 'USD';
      updateJourney({ categoriesCreated: true, step: 4, budgetCurrency: currency });
      if (activeServer) {
        onSaveServers(
          savedServers.map(server =>
            server.id === activeServer.id
              ? { ...server, defaultBudgetCurrency: currency }
              : server
          )
        );
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message === 'AUTH_REQUIRED') {
        setCategoriesError(t('steps.categories.authRequired', { ns: 'onboarding' }));
      } else if (message === 'NO_DEFAULT_BUDGET') {
        setCategoriesError(t('steps.categories.noBudget', { ns: 'onboarding' }));
      } else {
        setCategoriesError(
          message && message !== 'REQUEST_FAILED'
            ? message
            : t('steps.categories.createFailed', { ns: 'onboarding' })
        );
      }
    } finally {
      setIsCreatingCategories(false);
    }
  };


  const handleContinueFromAccounts = async () => {
    const accountsToSave = (
      answers.bankAccounts.length > 0
        ? answers.bankAccounts
        : [createEmptyBankAccount(0, getDefaultBankCountry(answers.primaryCountry), answers.primaryCurrency || 'USD')]
    )
      .filter(account => account.name.trim() && account.country && account.bankId)
      .map(account => ({ ...account, createInActual: true }));

    const nextAnswers = { ...answers, bankAccounts: accountsToSave };
    setAnswers(nextAnswers);

    const accountsPayload = buildAccountsFromAnswers(nextAnswers, countryLabel).map(account => ({
      name: account.name,
      accountType: account.accountType
    }));

    if (accountsPayload.length > 0 && activeServer && !accountsResult) {
      setIsCreatingAccounts(true);
      setAccountsError(null);
      try {
        const result = await createAccountsInBudget(activeServer, accountsPayload);
        setAccountsResult(result);
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (message === 'AUTH_REQUIRED') {
          setAccountsError(t('steps.accounts.authRequired', { ns: 'onboarding' }));
        } else if (message === 'NO_DEFAULT_BUDGET') {
          setAccountsError(t('steps.accounts.noBudget', { ns: 'onboarding' }));
        } else {
          setAccountsError(
            message && message !== 'REQUEST_FAILED'
              ? message
              : t('steps.accounts.createFailed', { ns: 'onboarding' })
          );
        }
        setIsCreatingAccounts(false);
        return;
      }
      setIsCreatingAccounts(false);
    }

    updateJourney({ step: 5 });
  };

  const handleContinueFromDebts = async () => {
    let exchangeRates = null;
    try {
      exchangeRates = await fetchLatestExchangeRates(resolvedBudgetCurrency);
    } catch {
      exchangeRates = null;
    }

    const payload = buildDebtCategoriesPayload(
      answers.debts,
      t,
      resolvedBudgetCurrency,
      exchangeRates
    );

    if (payload.length > 0 && activeServer) {
      setIsCreatingDebtCategories(true);
      setDebtsError(null);
      try {
        const result = await createCategoriesInBudget(activeServer, payload, {
          replaceExisting: false
        });
        setDebtsCategoriesResult(result);
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (message === 'AUTH_REQUIRED') {
          setDebtsError(t('steps.debts.authRequired', { ns: 'onboarding' }));
        } else if (message === 'NO_DEFAULT_BUDGET') {
          setDebtsError(t('steps.debts.noBudget', { ns: 'onboarding' }));
        } else {
          setDebtsError(
            message && message !== 'REQUEST_FAILED'
              ? message
              : t('steps.debts.createFailed', { ns: 'onboarding' })
          );
        }
        setIsCreatingDebtCategories(false);
        return;
      }
      setIsCreatingDebtCategories(false);
    }

    updateJourney({ step: 7 });
  };

  const handleContinueFromGoals = async () => {
    let exchangeRates = null;
    try {
      exchangeRates = await fetchLatestExchangeRates(resolvedBudgetCurrency);
    } catch {
      exchangeRates = null;
    }

    const budgetSummary = computeBudgetSummary(answers, resolvedBudgetCurrency, exchangeRates);
    const payload = buildGoalCategoriesPayload(
      answers.goals,
      t,
      resolvedBudgetCurrency,
      exchangeRates,
      { includeContributions: budgetSummary.canSaveForGoals }
    );

    if (payload.length > 0 && activeServer) {
      setIsCreatingGoalCategories(true);
      setGoalsError(null);
      try {
        const result = await createCategoriesInBudget(activeServer, payload, {
          replaceExisting: false
        });
        setGoalsCategoriesResult(result);
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (message === 'AUTH_REQUIRED') {
          setGoalsError(t('steps.goals.authRequired', { ns: 'onboarding' }));
        } else if (message === 'NO_DEFAULT_BUDGET') {
          setGoalsError(t('steps.goals.noBudget', { ns: 'onboarding' }));
        } else {
          setGoalsError(
            message && message !== 'REQUEST_FAILED'
              ? message
              : t('steps.goals.createFailed', { ns: 'onboarding' })
          );
        }
        setIsCreatingGoalCategories(false);
        return;
      }
      setIsCreatingGoalCategories(false);
    }

    updateJourney({ step: 8 });
  };

  // ---------- Hero (choose your path) ----------
  if (journey.status === 'not-started') {
    return (
      <div className="onboarding-journey">
        <div className="journey-hero">
          <div className="journey-icon">🌱</div>
          <h2>{t('hero.title', { ns: 'onboarding' })}</h2>
          <p className="journey-subtitle">{t('hero.subtitle', { ns: 'onboarding' })}</p>

          <div className="info-box journey-two-parts">
            <strong>{t('hero.twoPartsTitle', { ns: 'onboarding' })}</strong>
            <p>{t('hero.nimahRole', { ns: 'onboarding' })}</p>
            <p>{t('hero.actualRole', { ns: 'onboarding' })}</p>
          </div>

          <div className="journey-paths">
            <div className="journey-path-card guided">
              <div className="path-badge">{t('hero.recommended', { ns: 'onboarding' })}</div>
              <h3>{t('hero.guidedTitle', { ns: 'onboarding' })}</h3>
              <p>{t('hero.guidedDescription', { ns: 'onboarding' })}</p>
              <ol className="path-steps-preview">
                <li>{t('steps.account.label', { ns: 'onboarding' })}</li>
                <li>{t('steps.credentials.label', { ns: 'onboarding' })}</li>
                <li>{t('steps.categories.label', { ns: 'onboarding' })}</li>
                <li>{t('steps.bankAccounts.label', { ns: 'onboarding' })}</li>
                <li>{t('steps.statements.label', { ns: 'onboarding' })}</li>
                <li>{t('steps.debts.label', { ns: 'onboarding' })}</li>
                <li>{t('steps.goals.label', { ns: 'onboarding' })}</li>
              </ol>
              <button
                className="btn btn-journey-primary"
                onClick={() => updateJourney({ status: 'guided', step: 1, hostingChoice: 'managed' })}
              >
                {t('hero.startGuided', { ns: 'onboarding' })}
              </button>
            </div>

            <div className="journey-path-card explore">
              <h3>{t('hero.exploreTitle', { ns: 'onboarding' })}</h3>
              <p>{t('hero.exploreDescription', { ns: 'onboarding' })}</p>
              <ul className="path-features">
                <li>🔁 {t('hero.exploreFeatureConvert', { ns: 'onboarding' })}</li>
                <li>🏦 {t('hero.exploreFeatureBanks', { ns: 'onboarding' })}</li>
                <li>🔓 {t('hero.exploreFeatureNoAccount', { ns: 'onboarding' })}</li>
              </ul>
              <button className="btn btn-journey-secondary" onClick={onExploreTools}>
                {t('hero.exploreCta', { ns: 'onboarding' })}
              </button>
            </div>
          </div>

          <div className="journey-trust">
            <span>🔒 {t('hero.trustPrivacy', { ns: 'onboarding' })}</span>
            <span>⚡ {t('hero.trustSpeed', { ns: 'onboarding' })}</span>
            <span>🤖 {t('hero.trustAi', { ns: 'onboarding' })}</span>
          </div>
        </div>
      </div>
    );
  }

  if (journey.status !== 'guided') {
    return null;
  }

  const stepDone = [
    Boolean(user),
    journey.step >= 3,
    journey.categoriesCreated || journey.step >= 4,
    journey.step >= 5,
    journey.step >= 6,
    journey.step >= 7,
    journey.step >= 8
  ];
  const stepLabels = [
    t('steps.account.label', { ns: 'onboarding' }),
    t('steps.credentials.label', { ns: 'onboarding' }),
    t('steps.categories.label', { ns: 'onboarding' }),
    t('steps.bankAccounts.label', { ns: 'onboarding' }),
    t('steps.statements.label', { ns: 'onboarding' }),
    t('steps.debts.label', { ns: 'onboarding' }),
    t('steps.goals.label', { ns: 'onboarding' })
  ];

  const goToStep = (step: number) => updateJourney({ step });

  const provisionErrorMessage = provisionError
    ? mapConnectError(provisionError)
    : null;

  return (
    <div className="onboarding-journey">
      <div className="journey-wizard">
        <div className="wizard-header">
          <h2>{t('wizard.title', { ns: 'onboarding' })}</h2>
          <button className="wizard-exit" onClick={onExploreTools}>
            {t('wizard.exit', { ns: 'onboarding' })}
          </button>
        </div>

        <div className="wizard-stepper">
          {stepLabels.map((label, index) => {
            const stepNumber = index + 1;
            const isActive = journey.step === stepNumber;
            const isDone = stepDone[index];
            return (
              <React.Fragment key={label}>
                {index > 0 && <div className={`stepper-line ${stepDone[index - 1] ? 'done' : ''}`} />}
                <button
                  className={`stepper-item ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}
                  onClick={() => goToStep(stepNumber)}
                >
                  <span className="stepper-circle">{isDone ? '✓' : stepNumber}</span>
                  <span className="stepper-label">{label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 1: Create a Nimah account */}
        {journey.step === 1 && (
          <div className="wizard-step">
            <h3>{t('steps.account.title', { ns: 'onboarding' })}</h3>
            {user ? (
              <>
                <div className="success-box">
                  ✅ {t('steps.account.signedInAs', { ns: 'onboarding' })} <strong>{user.email}</strong>
                </div>
                <div className="wizard-actions">
                  <button className="btn btn-journey-primary" onClick={() => goToStep(2)}>
                    {t('wizard.continue', { ns: 'onboarding' })} →
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="step-description">{t('steps.account.description', { ns: 'onboarding' })}</p>
                <ul className="benefit-list">
                  <li>🔗 {t('steps.account.benefitConnect', { ns: 'onboarding' })}</li>
                  <li>📥 {t('steps.account.benefitImport', { ns: 'onboarding' })}</li>
                  <li>🏷️ {t('steps.account.benefitCategorize', { ns: 'onboarding' })}</li>
                </ul>
                <div className="wizard-actions">
                  <button className="btn btn-journey-primary" onClick={() => onShowAuthModal('signup')}>
                    {t('steps.account.createAccount', { ns: 'onboarding' })}
                  </button>
                </div>
                <p className="step-note">
                  {t('steps.account.alreadyHaveAccount', { ns: 'onboarding' })}{' '}
                  <button className="link-button" onClick={() => onShowAuthModal('signin')}>
                    {t('steps.account.signIn', { ns: 'onboarding' })}
                  </button>
                </p>
              </>
            )}
          </div>
        )}

        {/* Step 2: Save private password */}
        {journey.step === 2 && (
          <div className="wizard-step">
            <h3>{t('steps.credentials.title', { ns: 'onboarding' })}</h3>
            <p className="step-description">{t('steps.credentials.description', { ns: 'onboarding' })}</p>

            {!bootstrapPassword && !isConnected && (
              <div className="info-box">
                <p>{t('steps.credentials.waitingForServer', { ns: 'onboarding' })}</p>
              </div>
            )}

            {(provisionErrorMessage || hostedServer?.status === 'failed') && (
              <div className="error-box">
                <p>
                  {provisionErrorMessage ||
                    hostedServer?.lastError ||
                    t('steps.activate.provisionFailed', { ns: 'onboarding' })}
                </p>
                <button
                  type="button"
                  className="btn btn-journey-secondary"
                  disabled={hostedBusy}
                  onClick={() => {
                    void onRetryProvision();
                  }}
                >
                  {t('steps.activate.retryProvision', { ns: 'onboarding' })}
                </button>
              </div>
            )}

            {(bootstrapPassword || serverPassword || (isConnected && activeServer?.password)) && (
              <div className="info-box activate-password-reveal">
                <strong>{t('steps.activate.passwordRevealTitle', { ns: 'onboarding' })}</strong>
                <p>{t('steps.activate.passwordRevealWarning', { ns: 'onboarding' })}</p>
                <div className="activate-password-row">
                  <code className="activate-password-value">
                    {passwordVisible
                      ? bootstrapPassword || serverPassword || activeServer?.password || ''
                      : '••••••••••••••••'}
                  </code>
                  <button
                    type="button"
                    className="btn btn-journey-secondary"
                    onClick={() => setPasswordVisible(visible => !visible)}
                  >
                    {passwordVisible
                      ? t('steps.activate.hidePassword', { ns: 'onboarding' })
                      : t('steps.activate.showPassword', { ns: 'onboarding' })}
                  </button>
                  <button
                    type="button"
                    className="btn btn-journey-primary"
                    onClick={() => {
                      void copyBootstrapPassword();
                    }}
                  >
                    {passwordCopied
                      ? t('steps.activate.passwordCopied', { ns: 'onboarding' })
                      : t('steps.activate.copyPassword', { ns: 'onboarding' })}
                  </button>
                </div>
                <p className="step-note">{t('steps.credentials.protectsTransactions', { ns: 'onboarding' })}</p>
                <p className="step-note">{t('steps.credentials.keepSafe', { ns: 'onboarding' })}</p>
                <p className="step-note">
                  {t('steps.activate.passwordSecurityNotice', { ns: 'onboarding' })}
                </p>
              </div>
            )}

            {connectError && (
              <div className="error-box">
                <p>{connectError}</p>
              </div>
            )}

            {!bootstrapPassword && !isConnected && canOpenActual && (
              <div className="activate-connect-form">
                <p className="step-description">
                  {t('steps.activate.manualPasswordHint', { ns: 'onboarding' })}
                </p>
                <label htmlFor="activate-server-password">
                  {t('steps.activate.passwordLabel', { ns: 'onboarding' })}
                </label>
                <input
                  id="activate-server-password"
                  type="password"
                  value={serverPassword}
                  placeholder={t('steps.activate.passwordPlaceholder', { ns: 'onboarding' })}
                  onChange={e => setServerPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  className="btn btn-journey-primary"
                  onClick={() => {
                    void handleConnect();
                  }}
                  disabled={isConnecting || !serverPassword.trim()}
                >
                  {isConnecting
                    ? t('steps.activate.connecting', { ns: 'onboarding' })
                    : t('steps.activate.connectCta', { ns: 'onboarding' })}
                </button>
              </div>
            )}

            <div className="wizard-actions">
              <button className="btn btn-journey-secondary" onClick={() => goToStep(1)}>
                ← {t('wizard.back', { ns: 'onboarding' })}
              </button>
              <button
                className="btn btn-journey-primary"
                onClick={() => {
                  onClearBootstrapPassword();
                  updateJourney({ step: 3 });
                }}
              >
                {t('wizard.continue', { ns: 'onboarding' })} →
              </button>
            </div>
            <p className="step-note">
              <button className="link-button" onClick={onShowServerManagement}>
                {t('steps.activate.advancedLink', { ns: 'onboarding' })}
              </button>
            </p>
          </div>
        )}

        {/* Step 3: Categories */}
        {journey.step === 3 && (
          <div className="wizard-step">
            {categoriesPhase === 'questions' ? (
              <>
                <h3>{t('steps.categories.questionsTitle', { ns: 'onboarding' })}</h3>
                <p className="step-description">
                  {t('steps.categories.questionsDescription', { ns: 'onboarding' })}
                </p>
                {!isConnected && !(provisionErrorMessage || hostedServer?.status === 'failed') && (
                  <div className="info-box">
                    <p>{t('steps.categories.preparingInBackground', { ns: 'onboarding' })}</p>
                  </div>
                )}
                {(provisionErrorMessage || hostedServer?.status === 'failed') && (
                  <div className="error-box">
                    <p>
                      {provisionErrorMessage ||
                        hostedServer?.lastError ||
                        t('steps.activate.provisionFailed', { ns: 'onboarding' })}
                    </p>
                    <button
                      type="button"
                      className="btn btn-journey-secondary"
                      disabled={hostedBusy}
                      onClick={() => {
                        void onRetryProvision();
                      }}
                    >
                      {t('steps.activate.retryProvision', { ns: 'onboarding' })}
                    </button>
                  </div>
                )}

                <div className="category-builder-layout">
                  <OnboardingCategoryBuilder
                    answers={answers}
                    incomeSectionRef={incomeSectionRef}
                    onChange={next => {
                      setAnswers(next);
                      if (
                        next.primaryCurrency &&
                        next.primaryCurrency !== answers.primaryCurrency
                      ) {
                        updateJourney({ budgetCurrency: next.primaryCurrency });
                      }
                    }}
                  />
                  <OnboardingBudgetSummary
                    answers={answers}
                    budgetCurrency={resolvedBudgetCurrency}
                    onChange={setAnswers}
                    onGoToIncome={scrollToIncomeSection}
                  />
                </div>

                {categoriesError && (
                  <div className="error-box">
                    <p>{categoriesError}</p>
                  </div>
                )}

                <div className="wizard-actions">
                  <button className="btn btn-journey-secondary" onClick={() => goToStep(2)}>
                    ← {t('wizard.back', { ns: 'onboarding' })}
                  </button>
                  <button
                    className="btn btn-journey-primary"
                    onClick={() => void generateFromAnswers()}
                    disabled={isGeneratingCategories}
                  >
                    {isGeneratingCategories
                      ? t('steps.categories.generating', { ns: 'onboarding' })
                      : t('steps.categories.generateCta', { ns: 'onboarding' })}{' '}
                    →
                  </button>
                </div>
                <p className="step-note">
                  <button className="link-button" onClick={() => updateJourney({ step: 4 })}>
                    {t('steps.categories.skip', { ns: 'onboarding' })}
                  </button>
                </p>
              </>
            ) : (
              <>
                <h3>{t('steps.categories.reviewTitle', { ns: 'onboarding' })}</h3>
                <p className="step-description">
                  {t('steps.categories.reviewDescription', { ns: 'onboarding' })}
                </p>
                <div className="info-box">
                  <p>{t('steps.categories.replaceWarning', { ns: 'onboarding' })}</p>
                </div>
                {templatedCategoryCount > 0 && (
                  <div className="info-box">
                    <p>
                      {t('steps.categories.templatesReady', {
                        ns: 'onboarding',
                        count: templatedCategoryCount
                      })}
                    </p>
                  </div>
                )}

                <div className="category-groups">
                  {categoryGroups.map(group => (
                    <div key={group.id} className={`category-group-card ${group.enabled ? '' : 'disabled'}`}>
                      <div className="category-group-header">
                        <label className="group-toggle">
                          <input
                            type="checkbox"
                            checked={group.enabled}
                            onChange={() => toggleGroup(group.id)}
                          />
                          {group.isCustom ? (
                            <input
                              type="text"
                              className="group-name-input"
                              value={group.name}
                              placeholder={t('steps.categories.groupNamePlaceholder', { ns: 'onboarding' })}
                              onChange={e => renameGroup(group.id, e.target.value)}
                            />
                          ) : (
                            <span className="group-name">{group.name}</span>
                          )}
                        </label>
                        {group.isCustom && (
                          <button
                            className="remove-group-button"
                            onClick={() => removeGroup(group.id)}
                            aria-label={t('steps.categories.removeGroup', { ns: 'onboarding' })}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                      {group.enabled && (
                        <>
                          <div className="category-chips">
                            {group.categories.map((category, index) => (
                              <span key={`${category.name}-${index}`} className="category-chip">
                                <span className="chip-label">{category.name}</span>
                                <button
                                  className="chip-remove"
                                  onClick={() => removeCategory(group.id, index)}
                                  aria-label={t('steps.categories.removeCategory', { ns: 'onboarding' })}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                          <div className="add-category-row">
                            <input
                              type="text"
                              value={newCategoryInputs[group.id] || ''}
                              placeholder={t('steps.categories.addCategoryPlaceholder', { ns: 'onboarding' })}
                              onChange={e =>
                                setNewCategoryInputs(inputs => ({ ...inputs, [group.id]: e.target.value }))
                              }
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  addCategory(group.id);
                                }
                              }}
                            />
                            <button className="add-category-button" onClick={() => addCategory(group.id)}>
                              + {t('steps.categories.add', { ns: 'onboarding' })}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <button className="add-group-button" onClick={addCustomGroup}>
                  + {t('steps.categories.addGroup', { ns: 'onboarding' })}
                </button>

                {categoriesError && (
                  <div className="error-box">
                    <p>{categoriesError}</p>
                    {(provisionErrorMessage || hostedServer?.status === 'failed') && (
                      <button
                        type="button"
                        className="btn btn-journey-secondary"
                        disabled={hostedBusy}
                        onClick={() => {
                          void onRetryProvision();
                        }}
                      >
                        {t('steps.activate.retryProvision', { ns: 'onboarding' })}
                      </button>
                    )}
                  </div>
                )}

                <div className="wizard-actions">
                  <button
                    className="btn btn-journey-secondary"
                    onClick={() => updateJourney({ categoriesPhase: 'questions' })}
                  >
                    ← {t('steps.categories.backToQuestions', { ns: 'onboarding' })}
                  </button>
                  <button
                    className="btn btn-journey-primary"
                    onClick={handleCreateCategories}
                    disabled={isCreatingCategories || selectedCategoryCount === 0}
                  >
                    {isCreatingCategories
                      ? t('steps.categories.creating', { ns: 'onboarding' })
                      : t('steps.categories.createCta', { ns: 'onboarding', count: selectedCategoryCount })}
                  </button>
                </div>
                <p className="step-note">
                  <button className="link-button" onClick={() => updateJourney({ step: 4 })}>
                    {t('steps.categories.skip', { ns: 'onboarding' })}
                  </button>
                </p>
              </>
            )}
          </div>
        )}

        {/* Step 4: Bank accounts */}
        {journey.step === 4 && (
          <div className="wizard-step">
            <h3>{t('steps.bankAccounts.title', { ns: 'onboarding' })}</h3>
            <p className="step-description">{t('steps.bankAccounts.description', { ns: 'onboarding' })}</p>

            <OnboardingAccountsBuilder answers={answers} onChange={setAnswers} />

            {accountsResult && (
              <div className="success-box">
                ✅{' '}
                {t('steps.done.accountsSummary', {
                  ns: 'onboarding',
                  count: accountsResult.createdAccounts
                })}
                {accountsResult.skippedAccounts > 0 ? (
                  <>
                    {' '}
                    {t('steps.accounts.skippedSummary', {
                      ns: 'onboarding',
                      count: accountsResult.skippedAccounts
                    })}
                  </>
                ) : null}
              </div>
            )}

            {accountsError && (
              <div className="error-box">
                <p>{accountsError}</p>
              </div>
            )}

            <div className="wizard-actions">
              <button className="btn btn-journey-secondary" onClick={() => goToStep(3)}>
                ← {t('wizard.back', { ns: 'onboarding' })}
              </button>
              <button
                className="btn btn-journey-primary"
                onClick={() => void handleContinueFromAccounts()}
                disabled={isCreatingAccounts || namedAccounts.length === 0}
              >
                {isCreatingAccounts
                  ? t('steps.accounts.creating', { ns: 'onboarding' })
                  : t('wizard.continue', { ns: 'onboarding' })}{' '}
                →
              </button>
            </div>
            <p className="step-note">
              <button className="link-button" onClick={() => updateJourney({ step: 5 })}>
                {t('steps.bankAccounts.skip', { ns: 'onboarding' })}
              </button>
            </p>
          </div>
        )}


        {/* Step 5: Import statements */}
        {journey.step === 5 && (
          <div className="wizard-step">
            <h3>{t('steps.statements.title', { ns: 'onboarding' })}</h3>
            <p className="step-description">{t('steps.statements.description', { ns: 'onboarding' })}</p>

            <OnboardingStatementsStep
              answers={answers}
              onChange={setAnswers}
              statementFiles={statementFiles}
              onStatementFileChange={(accountId, file) =>
                setStatementFiles(prev => ({ ...prev, [accountId]: file }))
              }
            />

            {!hasAccountBalances && namedAccounts.length > 0 && (
              <div className="info-box">
                <p>{t('steps.statements.balancesNeeded', { ns: 'onboarding' })}</p>
              </div>
            )}

            <div className="wizard-actions">
              <button className="btn btn-journey-secondary" onClick={() => goToStep(4)}>
                ← {t('wizard.back', { ns: 'onboarding' })}
              </button>
              <button
                className="btn btn-journey-primary"
                disabled={!hasAccountBalances && namedAccounts.length > 0}
                onClick={() => updateJourney({ step: 6 })}
              >
                {t('steps.statements.continueWithBalances', { ns: 'onboarding' })} →
              </button>
            </div>
            <p className="step-note">
              <button className="link-button" onClick={() => updateJourney({ step: 6 })}>
                {t('steps.statements.skip', { ns: 'onboarding' })}
              </button>
            </p>
          </div>
        )}

        {/* Step 6: Debts */}
        {journey.step === 6 && (
          <div className="wizard-step">
            <h3>{t('steps.debts.title', { ns: 'onboarding' })}</h3>
            <p className="step-description">{t('steps.debts.description', { ns: 'onboarding' })}</p>

            <OnboardingDebtsStep answers={answers} onChange={setAnswers} />

            {debtsCategoriesResult && (
              <div className="success-box">
                ✅{' '}
                {t('steps.debts.createdSummary', {
                  ns: 'onboarding',
                  count: debtsCategoriesResult.createdCategories
                })}
              </div>
            )}

            {debtsError && (
              <div className="error-box">
                <p>{debtsError}</p>
              </div>
            )}

            <div className="wizard-actions">
              <button className="btn btn-journey-secondary" onClick={() => goToStep(5)}>
                ← {t('wizard.back', { ns: 'onboarding' })}
              </button>
              <button
                className="btn btn-journey-primary"
                onClick={() => void handleContinueFromDebts()}
                disabled={isCreatingDebtCategories}
              >
                {isCreatingDebtCategories
                  ? t('steps.debts.creating', { ns: 'onboarding' })
                  : t('wizard.continue', { ns: 'onboarding' })}{' '}
                →
              </button>
            </div>
            <p className="step-note">
              <button className="link-button" onClick={() => updateJourney({ step: 7 })}>
                {t('steps.debts.skip', { ns: 'onboarding' })}
              </button>
            </p>
          </div>
        )}

        {/* Step 7: Goals */}
        {journey.step === 7 && (
          <div className="wizard-step">
            <h3>{t('steps.goals.title', { ns: 'onboarding' })}</h3>
            <p className="step-description">{t('steps.goals.description', { ns: 'onboarding' })}</p>

            <OnboardingGoalsStep
              answers={answers}
              budgetCurrency={resolvedBudgetCurrency}
              onChange={setAnswers}
            />

            {goalsCategoriesResult && (
              <div className="success-box">
                ✅{' '}
                {t('steps.goals.createdSummary', {
                  ns: 'onboarding',
                  count: goalsCategoriesResult.createdCategories
                })}
              </div>
            )}

            {goalsError && (
              <div className="error-box">
                <p>{goalsError}</p>
              </div>
            )}

            <div className="wizard-actions">
              <button className="btn btn-journey-secondary" onClick={() => goToStep(6)}>
                ← {t('wizard.back', { ns: 'onboarding' })}
              </button>
              <button
                className="btn btn-journey-primary"
                onClick={() => void handleContinueFromGoals()}
                disabled={isCreatingGoalCategories}
              >
                {isCreatingGoalCategories
                  ? t('steps.goals.creating', { ns: 'onboarding' })
                  : t('wizard.continue', { ns: 'onboarding' })}{' '}
                →
              </button>
            </div>
            <p className="wizard-skip">
              <button type="button" className="link-button" onClick={() => updateJourney({ step: 8 })}>
                {t('steps.goals.skip', { ns: 'onboarding' })}
              </button>
            </p>
          </div>
        )}

        {/* Step 8: Done */}
        {journey.step === 8 && (
          <div className="wizard-step wizard-done">
            <div className="done-icon">🎉</div>
            <h3>{t('steps.done.title', { ns: 'onboarding' })}</h3>
            <p className="step-description">{t('steps.done.description', { ns: 'onboarding' })}</p>

            {creationResult && (
              <div className="success-box">
                ✅{' '}
                {t('steps.done.categoriesSummary', {
                  ns: 'onboarding',
                  groups: creationResult.createdGroups,
                  categories: creationResult.createdCategories
                })}
                {creationResult.notesApplied ? (
                  <>
                    {' '}
                    {t('steps.done.templatesSummary', {
                      ns: 'onboarding',
                      count: creationResult.notesApplied
                    })}
                  </>
                ) : null}
              </div>
            )}

            {accountsResult && (
              <div className="success-box">
                ✅{' '}
                {t('steps.done.accountsSummary', {
                  ns: 'onboarding',
                  count: accountsResult.createdAccounts
                })}
              </div>
            )}

            <ul className="benefit-list">
              <li>📊 {t('steps.done.nextBudget', { ns: 'onboarding' })}</li>
              <li>📄 {t('steps.done.nextStatements', { ns: 'onboarding' })}</li>
              <li>🏷️ {t('steps.done.nextCategorize', { ns: 'onboarding' })}</li>
              <li>📋 {t('steps.done.nextTemplates', { ns: 'onboarding' })}</li>
            </ul>

            <div className="wizard-actions">
              <button
                className="btn btn-journey-primary"
                onClick={() => updateJourney({ status: 'completed' })}
              >
                {t('steps.done.enterDashboard', { ns: 'onboarding' })} →
              </button>
            </div>
          </div>
        )}

        <p className="wizard-footer-note">{t('wizard.progressSaved', { ns: 'onboarding' })}</p>
      </div>
    </div>
  );
};

export default OnboardingJourney;
